import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ABOUT,
  ACCESS_DENIED,
  ANALYTICS,
  AUTH,
  BATTERY_LOGS,
  BILLING,
  COMMENTS,
  SALES,
  DEVICE_EVENTS,
  EVENTS,
  OFFLINE_HEADSETS,
  HEADSETS,
  headsetsPageMap,
  HOTELS,
  hotelsPageMap,
  MAIN,
  META,
  METRICS,
  pagesList,
  PAYMENTS,
  PROBLEMS,
  REPORTS,
  reportsPageMap,
  SERVICE,
  servicePageMap,
  SETTINGS,
  SUBSCRIPTION,
  USER_EVENTS,
} from 'src/constants';
import { Company } from 'src/models';
import { BreadCrumb, PageList } from 'src/interface';
import { rulesMap } from 'src/security/roles';
import { useLanguage, usePopUpNotifications } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import {
  useAccountState,
  fetchAccountAction,
  fetchCompaniesStatisticsAction,
} from 'src/services/auth';
import { useHeadsetById, fetchHeadsetAction } from 'src/services/headsets';
import { useCompanyById, fetchCompanyAction } from 'src/services/company';

export const useNavigation = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const { user, isPending } = useAccountState();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const company = useCompanyById(companyId) as Company;
  const {
    page,
    homePage,
    access: { currentAccess },
  } = usePage();
  const { addErrorNotification } = usePopUpNotifications();

  const invalidCompany = useMemo(
    () =>
      Boolean(
        !isPending &&
          user &&
          companyId &&
          !user.companies.find(({ id }) => id === companyId),
      ),
    [companyId, isPending, user],
  );

  useEffect(() => {
    dispatch(fetchAccountAction());
  }, [dispatch]);

  useEffect(() => {
    if (isPending || !user || !user.companies.length) {
      return;
    }

    dispatch(fetchCompaniesStatisticsAction());
  }, [dispatch, isPending, user]);

  useEffect(() => {
    if (
      isPending ||
      !user ||
      !companyId ||
      !user.companies.find(({ id }) => id === companyId) ||
      company
    ) {
      return;
    }

    dispatch(fetchCompanyAction({ id: companyId }));
  }, [dispatch, isPending, user, companyId, company]);

  useEffect(() => {
    if (
      isPending ||
      !user ||
      !companyId ||
      !headsetId ||
      !user.companies.find(({ id }) => id === companyId)
    ) {
      return;
    }

    dispatch(fetchHeadsetAction({ companyId, headsetId }));
  }, [dispatch, isPending, user, companyId, headsetId]);

  useEffect(() => {
    if (invalidCompany && homePage) {
      navigate(homePage);
      addErrorNotification('error.CompanyAccessDenied');
    }
  }, [homePage, invalidCompany, navigate, addErrorNotification]);

  useEffect(() => {
    if (isPending) {
      return;
    }

    if (!user && page !== AUTH) {
      navigate(`/${AUTH}`);
      return;
    }

    if (user && page !== null) {
      if (!user.companies.length && page !== ACCESS_DENIED) {
        navigate(`/${ACCESS_DENIED}`);
        return;
      }

      if (!currentAccess && homePage) {
        navigate(homePage);
      }
    }
  }, [user, isPending, page, currentAccess, homePage, navigate, companyId]);
};

const emptyPageParams = {
  header: null,
  backUrl: null,
  breadCrumbs: null,
  pageList: null,
};

export const usePage = (): {
  group: string | null;
  page: string | null;
  homePage: string | null;
  access: {
    currentAccess: boolean | null;
    accessMap: Record<string, string[]>;
    hotelsPageAccess: boolean | null;
    mainPageAccess: boolean | null;
  };
} => {
  const { pathname } = useLocation();
  const { user, isPending } = useAccountState();

  const { group, page } = useMemo(() => {
    if (pathname === '/') {
      return {
        group: null,
        page: AUTH,
      };
    }

    const patches = pathname.split('/');

    if ([MAIN, HOTELS, ACCESS_DENIED].includes(patches[1]) && !patches[2]) {
      return {
        group: null,
        page: patches[1],
      };
    }

    if (
      (patches[1] === REPORTS &&
        [SALES, PROBLEMS, BILLING, OFFLINE_HEADSETS].includes(patches[2])) ||
      (patches[1] === SERVICE &&
        [HEADSETS, DEVICE_EVENTS, ANALYTICS].includes(patches[2]))
    ) {
      return {
        group: patches[1],
        page: patches[2],
      };
    }

    if (
      patches[1] === HOTELS &&
      [HEADSETS, METRICS, ANALYTICS, BILLING, ABOUT].includes(patches[3]) &&
      !patches[4]
    ) {
      return {
        group: HOTELS,
        page: patches[3],
      };
    }

    if (
      patches[1] === HOTELS &&
      patches[3] === HEADSETS &&
      [
        SETTINGS,
        SUBSCRIPTION,
        BILLING,
        EVENTS,
        USER_EVENTS,
        PAYMENTS,
        COMMENTS,
        META,
        BATTERY_LOGS,
      ].includes(patches[5])
    ) {
      return {
        group: HEADSETS,
        page: patches[5],
      };
    }

    return {
      group: null,
      page: null,
    };
  }, [pathname]);

  const accessMap = useMemo(
    () =>
      [REPORTS, SERVICE, HOTELS, HEADSETS].reduce(
        (result, gr) => {
          if (isPending || !user || !user.companies.length) {
            return {
              ...result,
              [gr]: [],
            };
          }

          return {
            ...result,
            [gr]: pagesList[gr].filter((pl) => {
              const isIncludes = rulesMap[gr][pl].includes(user.role);

              if (
                isIncludes &&
                pl === BATTERY_LOGS &&
                ![
                  'd1991v8p@mail.ru',
                  'cleric57@gmail.com',
                  'polya.kluch@yandex.ru',
                  'cnayhlu2@gmail.com',
                  'kryuchkova.alina7@gmail.com',
                  'echistihina@yandex.ru',
                  'levelgd@gmail.com',
                  'maximshalyutov@gmail.com',
                ].includes(user.email)
              ) {
                return false;
              }

              return isIncludes;
            }),
          };
        },
        {} as Record<string, string[]>,
      ),
    [user, isPending],
  );

  const hotelsPageAccess = useMemo(
    () =>
      isPending || !user?.companies.length || page === null
        ? null
        : accessMap[HOTELS].length > 0 && user.companies.length > 1,
    [accessMap, isPending, page, user?.companies.length],
  );

  const mainPageAccess = useMemo(
    () =>
      isPending || !user?.companies.length || page === null
        ? null
        : Number(Boolean(accessMap[REPORTS].length)) +
            Number(Boolean(accessMap[HOTELS].length)) +
            Number(Boolean(accessMap[SERVICE].length)) >
          1,
    [accessMap, isPending, page, user?.companies.length],
  );

  const homePage = useMemo(() => {
    if (isPending || !user?.companies.length) {
      return null;
    }

    if (mainPageAccess) {
      return `/${MAIN}`;
    }

    if (hotelsPageAccess) {
      return `/${HOTELS}`;
    }

    if (accessMap[HOTELS].length) {
      return `/${HOTELS}/${user.companies[0].id}/${accessMap[HOTELS][0]}`;
    }

    if (accessMap[REPORTS].length) {
      return `/${REPORTS}/${accessMap[REPORTS][0]}`;
    }

    if (accessMap[SERVICE].length) {
      return `/${SERVICE}/${accessMap[SERVICE][0]}`;
    }

    return null;
  }, [accessMap, mainPageAccess, hotelsPageAccess, isPending, user?.companies]);

  const hasAccess = useMemo(() => {
    if (isPending || !user?.companies.length || page === null) {
      return null;
    }

    if (page === MAIN) {
      return mainPageAccess;
    }

    if (page === HOTELS) {
      return hotelsPageAccess;
    }

    if (group && [REPORTS, SERVICE, HOTELS, HEADSETS].includes(group)) {
      return accessMap[group]?.includes(page) ?? null;
    }

    return [ACCESS_DENIED].includes(page);
  }, [
    accessMap,
    mainPageAccess,
    hotelsPageAccess,
    group,
    isPending,
    page,
    user?.companies.length,
  ]);

  return {
    group,
    page,
    homePage,
    access: {
      currentAccess: hasAccess,
      accessMap: accessMap,
      mainPageAccess,
      hotelsPageAccess,
    },
  };
};

export const usePageParams = (): {
  header: string | null;
  backUrl: string | null;
  breadCrumbs: BreadCrumb[] | null;
  pageList: PageList | null;
} => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const { t, td } = useLanguage();
  const { pathname } = useLocation();
  const {
    group,
    page,
    access: { currentAccess, accessMap, mainPageAccess, hotelsPageAccess },
  } = usePage();
  const headset = useHeadsetById(headsetId);
  const company = useCompanyById(companyId) as Company;

  return useMemo(() => {
    if (currentAccess === null || page === null) {
      return emptyPageParams;
    }

    if (page === MAIN) {
      return {
        header: t('pages.homePage'),
        backUrl: null,
        breadCrumbs: null,
        pageList: null,
      };
    }

    if (group === REPORTS) {
      return {
        header: td(
          accessMap[REPORTS].length === 1
            ? reportsPageMap[accessMap[REPORTS][0]]
            : 'pages.reportsPage',
        ),
        backUrl: mainPageAccess ? `/${MAIN}` : null,
        breadCrumbs: mainPageAccess
          ? [
              {
                url: `/${MAIN}`,
                label: t('pages.homePage'),
              },
              {
                label: td(
                  accessMap[REPORTS].length === 1
                    ? reportsPageMap[accessMap[REPORTS][0]]
                    : 'pages.reportsPage',
                ),
              },
            ]
          : null,
        pageList:
          accessMap[REPORTS].length === 1
            ? null
            : {
                rootUrl: `/${REPORTS}`,
                currentId: page,
                list: accessMap[REPORTS].map((id) => ({
                  id,
                  label: td(reportsPageMap[id]),
                })),
              },
      };
    }

    if (group === SERVICE) {
      return {
        header: td(
          accessMap[SERVICE].length === 1
            ? servicePageMap[accessMap[SERVICE][0]]
            : 'pages.servicePage',
        ),
        backUrl: mainPageAccess ? `/${MAIN}` : null,
        breadCrumbs: mainPageAccess
          ? [
              {
                url: `/${MAIN}`,
                label: t('pages.homePage'),
              },
              {
                label: td(
                  accessMap[SERVICE].length === 1
                    ? servicePageMap[accessMap[SERVICE][0]]
                    : 'pages.servicePage',
                ),
              },
            ]
          : null,
        pageList:
          accessMap[SERVICE].length === 1
            ? null
            : {
                rootUrl: `/${SERVICE}`,
                currentId: page,
                list: accessMap[SERVICE].map((id) => ({
                  id,
                  label: td(servicePageMap[id]),
                })),
              },
      };
    }

    if (page === HOTELS) {
      return mainPageAccess
        ? {
            header: t('pages.hotelsPage'),
            backUrl: `/${MAIN}`,
            breadCrumbs: [
              {
                url: `/${MAIN}`,
                label: t('pages.homePage'),
              },
              {
                label: t('pages.hotelsPage'),
              },
            ],
            pageList: null,
          }
        : {
            header: t('pages.hotelsPage'),
            backUrl: null,
            breadCrumbs: null,
            pageList: null,
          };
    }

    if (group === HOTELS) {
      const companyName = company ? company.name : t('pages.hotelsPage');
      let backUrl: string | null = `/${HOTELS}`;
      let breadCrumbs: BreadCrumb[] = [
        mainPageAccess
          ? {
              url: `/${MAIN}`,
              label: t('pages.homePage'),
            }
          : null,
        {
          url: `/${HOTELS}`,
          label: t('pages.hotelsPage'),
        },
        company
          ? {
              label: company.name,
            }
          : null,
      ];

      if (!hotelsPageAccess) {
        backUrl = mainPageAccess ? `/${MAIN}` : null;
        breadCrumbs = mainPageAccess
          ? [
              {
                url: `/${MAIN}`,
                label: t('pages.homePage'),
              },
              company
                ? {
                    label: company.name,
                  }
                : null,
            ]
          : [
              {
                label: companyName,
              },
            ];
      }

      return {
        header:
          accessMap[HOTELS].length === 1
            ? `${td(hotelsPageMap[accessMap[HOTELS][0]])} [${companyName}]`
            : companyName,
        backUrl,
        breadCrumbs,
        pageList:
          accessMap[HOTELS].length === 1
            ? null
            : {
                rootUrl: `/${HOTELS}/${company?.id}`,
                currentId: page,
                list: accessMap[HOTELS].map((id) => ({
                  id,
                  label: td(hotelsPageMap[id]),
                })),
              },
      };
    }

    if (group === HEADSETS) {
      const backUrl = company
        ? `/${HOTELS}/${company.id}/${accessMap[HOTELS][0]}`
        : pathname;

      return {
        header: t('navigation.headset', { headset: headset?.label || '...' }),
        backUrl,
        breadCrumbs: [
          mainPageAccess
            ? {
                url: `/${MAIN}`,
                label: t('pages.homePage'),
              }
            : null,
          hotelsPageAccess
            ? {
                url: `/${HOTELS}`,
                label: t('pages.hotelsPage'),
              }
            : null,
          company
            ? {
                url: `/${HOTELS}/${company.id}/${accessMap[HOTELS][0]}`,
                label: company.name,
              }
            : {
                label: '...',
              },
          headset
            ? {
                label: t('navigation.headset', { headset: headset.label }),
              }
            : null,
        ],
        pageList:
          accessMap[HEADSETS].length === 1
            ? null
            : {
                rootUrl: `/${HOTELS}/${company?.id}/${HEADSETS}/${headset?.id}`,
                currentId: page,
                list: accessMap[HEADSETS].map((id) => ({
                  id,
                  label: td(headsetsPageMap[id]),
                })),
              },
      };
    }

    return emptyPageParams;
  }, [
    pathname,
    page,
    group,
    accessMap,
    mainPageAccess,
    hotelsPageAccess,
    currentAccess,
    t,
    td,
    company,
    headset,
  ]);
};
